import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { PropertySchemaMap } from '@/lib/crm/hooks/usePropertySchema';
import type { UploadedFileEntry } from '@/lib/csv-import/api';

import { fetchPropertyTypes, SCHEMA_STALE_TIME } from '../review/summary/types';

export interface UseEntitySchemaMapResult {
  schemaMap: PropertySchemaMap;
  /** Per-entity-type schema maps, keyed by `entity_type_name`. Use these when
   *  the consumer knows which entity type it is rendering (e.g. the per-type
   *  review grids) so cross-entity property-name collisions (`ahoy_name` is
   *  "Company Name" on companies but "Deal Name" on deals) resolve to the
   *  correct label. The flat `schemaMap` remains for contexts where a row's
   *  entity type is unknowable (see the known-limitation note below). */
  schemaMapByEntityType: Record<string, PropertySchemaMap>;
  allSchemasLoaded: boolean;
  isError: boolean;
}

/**
 * Fetches property-type schemas for every distinct entity type referenced by
 * `uploadedFiles`, deduped by `entity_type_name`, and merges the results into
 * a single {@link PropertySchemaMap}. Shared by LargeImportSummary and
 * CoercionReview, which both need the schema map to render property labels.
 *
 * Known limitation — cross-entity property-name collisions in the FLAT map:
 * when an import mixes entity types that share a `property_type_name` (e.g.
 * `ahoy_name` on both companies and deals), the flat merge keeps the schema
 * of the last distinct entity type in input order. The winner is
 * deterministic merge order, not request completion order: the merge iterates
 * `schemaQueries` (built from `entityTypeNames`, derived from `uploadedFiles`
 * order) and later entries overwrite earlier ones regardless of which fetch
 * finished first. Consumers that know their entity type must use
 * `schemaMapByEntityType` instead, which has no collisions. The flat map
 * remains for the large-import path only: the `/rows` endpoint deliberately
 * omits both `file_id` and `entity_type_name` from each {@link AnalyzedRow},
 * so a row's entity type is unknowable there and the problem-rows table
 * renders a flat, cross-file page (an accepted design decision — see the
 * plan's key decisions). If a future `/rows` payload carries per-row entity
 * type, select from `schemaMapByEntityType` per row and drop the flat map.
 */
export function useEntitySchemaMap(
  orgId: number,
  uploadedFiles: UploadedFileEntry[]
): UseEntitySchemaMapResult {
  const entityTypeNames = useMemo(() => {
    const types = new Set<string>();
    for (const file of uploadedFiles) {
      types.add(file.entity_type_name);
    }
    return Array.from(types);
  }, [uploadedFiles]);

  const schemaQueries = useQueries({
    queries: entityTypeNames.map(typeName => ({
      queryKey: ['propertySchema', orgId, typeName],
      queryFn: () => fetchPropertyTypes(orgId, typeName),
      staleTime: SCHEMA_STALE_TIME,
    })),
  });

  const allSchemasLoaded = schemaQueries.every(q => q.isSuccess);
  const isError = schemaQueries.some(q => q.isError);

  // Version key that changes whenever any query's data is (re)fetched —
  // including background refetches and cache updates that keep every query
  // `isSuccess`. Keying the memo on this (instead of `allSchemasLoaded`)
  // keeps the map current after fresh data arrives, while still returning a
  // stable reference when nothing changed.
  const schemaDataVersion = schemaQueries.map(q => q.dataUpdatedAt).join(',');

  const { schemaMap, schemaMapByEntityType } = useMemo(() => {
    const flat: PropertySchemaMap = {};
    const byEntityType: Record<string, PropertySchemaMap> = {};
    for (const [index, query] of schemaQueries.entries()) {
      if (query.data) {
        const typeMap: PropertySchemaMap = {};
        for (const pt of query.data) {
          flat[pt.property_type_name] = pt;
          typeMap[pt.property_type_name] = pt;
        }
        byEntityType[entityTypeNames[index]] = typeMap;
      }
    }
    return { schemaMap: flat, schemaMapByEntityType: byEntityType };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute when query data changes (schemaDataVersion), not on every schemaQueries array-identity change
  }, [schemaDataVersion, entityTypeNames]);

  return { schemaMap, schemaMapByEntityType, allSchemasLoaded, isError };
}
